import { useCallback, useEffect, useState } from 'react';
import { getEmailsEnvoyes } from '../services/emailJournalService';
import { formatDate } from './devisUtils';

/** Les types de modèles, en français lisible — le reste s'affiche tel quel. */
const TYPES_EMAIL = {
  devis: 'Devis',
  devis_relance: 'Relance devis',
  contrat: 'Contrat',
  contrat_relance: 'Relance contrat',
  convocation: 'Convocation',
  convocation_formateur: 'Convocation formateur',
  feuille_presence_signee: 'Feuille de présence signée',
  attestation: 'Attestations',
  documents_rappel: 'Rappel des pièces',
  formation_rappel: 'Rappel de formation',
  formation_rappel_formateur: 'Rappel de formation (formateur)',
  formation_jour_j: "Lien d'émargement",
  formation_jour_j_formateur: "Lien d'émargement (formateur)",
  emargement_relance: "Relance d'émargement",
  formation_satisfaction: 'Questionnaire de satisfaction',
  formation_satisfaction_formateur: 'Questionnaire de satisfaction (formateur)',
  notification_client: 'Notification interne',
};

/** « 3 oct. 2026 à 14:05 ». */
function formatInstant(valeur) {
  if (!valeur) return '—';
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(valeur)} à ${d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Les emails envoyés d'un dossier, pour les onglets « Emails » des fiches
 * (demande, client, formateur) : chaque ligne se déplie sur l'email tel
 * qu'il est parti — objet, corps, pièces jointes — et les échecs se voient.
 *
 * `filtres` : { contactId } ou { clientId } ou { formateurId }…, passés tels
 * quels au journal.
 */
function EmailsEnvoyes({ filtres = {} }) {
  const [emails, setEmails] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(null);

  const cle = JSON.stringify(filtres);

  const charger = useCallback(() => {
    setChargement(true);
    setErreur(null);
    return getEmailsEnvoyes({ ...JSON.parse(cle), page, limit: 20 })
      .then((res) => {
        setEmails(res.data || []);
        setPagination(res.pagination || { page: 1, limit: 20, total: 0 });
      })
      .catch((err) => setErreur(err.message || 'Impossible de charger les emails.'))
      .finally(() => setChargement(false));
  }, [cle, page]);

  useEffect(() => {
    charger();
  }, [charger]);

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  if (chargement) return <div className="devis-state">Chargement...</div>;
  if (erreur) return <div className="devis-state devis-state-error">{erreur}</div>;
  if (!emails.length) {
    return (
      <div className="devis-state">
        Aucun email envoyé pour ce dossier — les envois à venir s'enregistreront ici,
        tels qu'ils sont partis.
      </div>
    );
  }

  return (
    <div>
      {emails.map((e) => {
        const deplie = ouvert === e.id;
        const pieces = Array.isArray(e.pieces_jointes) ? e.pieces_jointes : [];
        return (
          <div
            key={e.id}
            style={{
              border: '1px solid #e2e2ea',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 8,
              cursor: 'pointer',
              background: e.statut === 'echec' ? '#fff5f5' : '#fff',
            }}
            onClick={() => setOuvert(deplie ? null : e.id)}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'baseline',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="devis-contact-name texte-borne">{e.sujet || '(sans objet)'}</div>
                <div className="devis-status-detail">
                  à {e.destinataire}
                  {e.type_modele ? ` · ${TYPES_EMAIL[e.type_modele] || e.type_modele}` : ''}
                  {e.envoye_par ? ` · par ${e.envoye_par}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span
                  className={`devis-status-pill${e.statut === 'echec' ? '' : ' devis-pill-valide'}`}
                >
                  {e.statut === 'echec' ? 'Échec' : 'Envoyé'}
                </span>
                <div className="devis-status-detail">{formatInstant(e.envoye_le)}</div>
              </div>
            </div>

            {deplie && (
              <div style={{ marginTop: 10 }} onClick={(ev) => ev.stopPropagation()}>
                {e.statut === 'echec' && e.erreur && (
                  <div className="devis-alert devis-alert-erreur" style={{ marginBottom: 8 }}>
                    {e.erreur}
                  </div>
                )}
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    lineHeight: 1.55,
                    background: '#f7f7fa',
                    borderRadius: 8,
                    padding: '12px 14px',
                    margin: 0,
                  }}
                >
                  {e.texte || '(corps vide)'}
                </pre>
                {pieces.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {pieces.map((p) => (
                      <span key={p} className="devis-status-pill">
                        📎 {p}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {pagination.total > pagination.limit && (
        <div className="devis-pagination">
          <span className="devis-pagination-info">
            {pagination.total} email{pagination.total > 1 ? 's' : ''}
          </span>
          <div className="devis-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Précédent
            </button>
            <span className="devis-pagination-page">
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailsEnvoyes;
